import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import dayjs from "dayjs";
import { AlertCircle, ChevronDown } from "lucide-react-native";
import { ComponentProps, useState } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { Button, ButtonIcon, ButtonText } from "../../ui/button";
import { Popover, PopoverBackdrop, PopoverContent } from "../../ui/popover";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";
import DatePickerFormModal from "./PickerWrapper";

type FormInputPopoverDateProp<TFieldValues extends FieldValues> =
  FormInputProps<TFieldValues, Date | null | undefined> & {
    buttonProps?: ComponentProps<typeof Button>;
    labelTextProps?: ComponentProps<typeof FormControlLabelText>;
    minDate?: Date;
  };

export default function FormInputPopoverDate<TFieldValues extends FieldValues>(
  props: FormInputPopoverDateProp<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    labelSize = "md",
    placeholder,
    required,
    disabled,
    className,
    buttonProps = {
      variant: "link",
      action: "secondary",
    },
    size,
    labelTextProps = {},
    minDate,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, value, ref, onBlur },
        fieldState: { error },
      }) => {
        const onConfirm = (date: Date) => {
          onChange(date);
          date.getTime() !== value?.getTime?.() && setIsOpen(false);
        };
        return (
          <FormControl
            isInvalid={!!error}
            isRequired={required}
            isDisabled={disabled}
            className={className}
            size={size}
            ref={focusScrollRef(ref)}
          >
            {label && (
              <FormControlLabel>
                <FormControlLabelText size={labelSize} {...labelTextProps}>
                  {label}
                </FormControlLabelText>
              </FormControlLabel>
            )}
            <Popover
              placement="bottom"
              offset={5}
              trapFocus={false}
              focusScope={false}
              size="lg"
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
              onOpen={() => setIsOpen(true)}
              trigger={({ ...triggerProps }) => {
                return (
                  <Button size={size} {...buttonProps} {...triggerProps}>
                    <ButtonText className="flex-1 shrink font-normal">
                      {value ? dayjs(value).format("DD/MM/YYYY") : placeholder}
                    </ButtonText>
                    <ButtonIcon as={ChevronDown} />
                  </Button>
                );
              }}
            >
              <PopoverBackdrop />
              <PopoverContent>
                <DatePickerFormModal
                  date={value}
                  onConfirm={onConfirm}
                  onBlur={onBlur}
                  minDate={minDate}
                />
              </PopoverContent>
            </Popover>
            <FormControlError className="items-start gap-x-2">
              <FormControlErrorIcon as={AlertCircle} className="mt-1" />
              <FormControlErrorText className="flex-1 shrink">
                {error?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
